import type { IFormulaValidation } from 'types'
import type { ThemePreference } from 'helpers/theme'
import {
  IHeadingStyle,
  INotification,
  IUserNotification,
  IUserNotificationListMetadata,
  EmailPreferences,
  ResponsePayload,
  IProjectSecret,
  ITableWithCells,
  IFile,
  IFileParentResource,
  ISignedUrlToken,
  ILogObject,
  ITable,
  ICommentObject,
  ICommentThreadObject,
  IAccessPolicy,
  IUserObject,
  ISummaryUser,
  IProjectMember,
  ITag,
  IProject,
  ICommentThreadStats,
  IContext,
  IOAuthConnectUrl,
  IMemberInvite,
  I2FaSetup,
  ResponseError,
  IProcess,
  IApiKey,
  ICeleryStats,
  IProcessObject,
  IProcessSectionObject,
  IFolderContents,
  IHubFolder,
  ISidebarContainers,
  ISidebarMoveItem,
  CommentThreadStatusType,
  CommentReferenceType,
  IAchievementsObject,
  IPinnedProjectObject,
  IUsed,
  ITableRow,
  IIntegrationRedirects,
  IIntegrationCatalogue,
  IIntegrationPassthroughResponse,
  IIntegrationData,
  ISubscriptionComparison,
  ITableViewWithColumns,
  EditorContent,
  ITableViewColumn,
  IColumnAlterOptions,
  ITableRowCompressed,
  ICellValue,
  INewView,
  ICreateTableJoin,
  ITableJoin,
  IFormulaHelper,
  IDuplicatedChild,
  IOrganisation,
  IOrganisationTeam,
  IOrganisationTeamDetail,
  ITeamsListResponse,
  IUserSession,
  IStuckLock,
  ISite,
  ISiteUserRow,
  IUserToSite,
  FieldOption,
  IAgentStreamEvent,
  IScriptInventoryRow,
  IDependencyOverview,
  IDependencyExpansion,
  IColumnarBatch,
  IColumnarColumnPayload
} from 'types'
import {
  UserRoles,
  AccessResourceKinds,
  AccessAttributeKinds,
  AUTH_KEY,
  FREE_TRIAL_BANNER_DISMISSED_KEY,
  WEB_CLIENT_QUERY
} from 'app-constants'
import { downloadFileToClient } from 'helpers/fileDownload'
import { getAuthToken, isSessionExpired, clearMasqueradeToken, markLoggedOut } from 'helpers/auth'
import { safeLocalStorage } from 'helpers/localStorage'
import { saveCurrentUrlForRedirect } from 'helpers/redirect'
import { convertToApiFilter } from 'components/spreadsheet/helpers/functions'
import type { IExportTableViewBody } from 'components/spreadsheet/helpers/export'
import { ICreateTableColumnView } from 'components/spreadsheet/contexts/data/actions'
import { decodeMultiStream } from '@msgpack/msgpack'
import { SpreadsheetInProcessContext } from 'components/spreadsheet/types'
import { post } from 'helpers/http-service'
import { config } from 'config'
import { SIGN_IN } from 'routes'
import * as Sentry from '@sentry/browser'
import { requestWithRetry, SERVER_ERROR_MESSAGE } from 'helpers/apiRetry'
import { APIError, ApiErrorCodes } from 'helpers/apiError'
import type { ApiErrorLink, HttpMethod } from 'helpers/apiError'
import type { ViewGrids } from 'components/document/components/modal/export/types'

// APIError / ApiErrorCodes used to be defined in this file. They now live in
// helpers/apiError so helpers/apiRetry can reuse them without pulling in this
// module's heavy dependency graph. Re-exported here for backwards compatibility.
export { APIError, ApiErrorCodes }
export type { ApiErrorLink, HttpMethod }

const apiUrl = config.API_HOST
const apiVersion = config.API_VERSION
export const apiPrefix = `${apiUrl}${apiVersion}`

type methods = HttpMethod

export interface FetchParams {
  method: methods
  headers: any
  mode?: 'cors' | 'navigate' | 'no-cors' | undefined
  credentials?: 'omit' | 'include' | 'same-origin'
  body?: string
  signal?: AbortSignal
}

// Transforms an object into an url query to be used in api - GET calls.
function objectUrlQueryfy(data: any): string {
  // If data is null or not valid return empty query.
  if (!data) {
    return ''
  }
  let query = ''
  // iterate throw the properties
  for (const property in data) {
    // do not use  !data[property] check otherwise it will remove Zero or false values.
    if (Object.prototype.hasOwnProperty.call(data, property) && data[property] != null && data[property] !== '') {
      query = query ? query + '&' : '?'
      if (Array.isArray(data[property]) && data[property].length > 0) {
        query += property + '=' + data[property].join(`&${property}=`)
      } else {
        query += property + '=' + encodeURIComponent(data[property])
      }
    }
  }
  return query
}

async function sendRequest({
  method = 'GET',
  endpoint,
  data,
  rawBody = false,
  headers = null,
  signal = undefined
}: ApiParams): Promise<Response> {
  let url = `${apiPrefix}${endpoint}`
  const params: FetchParams = {
    method,
    mode: 'cors',
    headers: headers || new Headers(),
    credentials: 'include'
  }

  if (signal) {
    params.signal = signal
  }

  // Don't cache
  params.headers.set('Cache-Control', 'no-cache')

  if (!rawBody && data && method !== 'GET') {
    params.headers.set('Content-Type', 'application/json')
  }

  const authToken = getAuthToken()
  if (authToken) {
    params.headers.set('Authorization', authToken)
  }

  // Set the body for the requests except for GET.
  if (data && method !== 'GET') {
    params.body = rawBody ? data : JSON.stringify(data)
  }
  // GET Method - Adds a query to the URL if a data object is provided.
  if (data && method === 'GET') {
    url += objectUrlQueryfy(data)
  }
  return fetch(url, params)
}

export interface ApiParams {
  method: methods
  endpoint: string
  data?: any
  rawBody?: boolean
  headers?: any
  signal?: AbortSignal
}

const PUBLIC_ENDPOINTS = [
  '/authenticate',
  '/send-password-reset-email',
  '/send-verification-email',
  '/verify-account',
  '/reset-password',
  '/refresh-token', // Allow refresh even if session appears expired
  '/identities/google/complete',
  '/identities/microsoft/complete',
  '/identities/google/connect',
  '/identities/microsoft/connect',
  '/identities/sso/connect',
  '/identities/allowed-methods',
  '/subscription/comparison'
]

const isPublicEndpoint = (endpoint: string): boolean => {
  if (PUBLIC_ENDPOINTS.some((publicEndpoint) => endpoint.includes(publicEndpoint))) {
    return true
  }
  // /user (exact match, POST for registration) doesn't require auth, but /user/* endpoints do
  if (endpoint === '/user') {
    return true
  }
  return false
}

const handleTokenExpired = (params: ApiParams): never => {
  markLoggedOut()
  safeLocalStorage.removeItem(AUTH_KEY)
  safeLocalStorage.removeItem(FREE_TRIAL_BANNER_DISMISSED_KEY)
  clearMasqueradeToken()
  Sentry.setUser(null)
  saveCurrentUrlForRedirect()
  window.location.href = `${SIGN_IN}?reason=sessionExpired`
  throw new APIError(
    ApiErrorCodes.UNAUTHORIZED,
    401,
    'Session expired – redirected to sign in',
    undefined,
    params.endpoint,
    params.method || 'GET'
  )
}

// Non-OK handler for the streaming / download paths that bypass the api() retry
// wrapper (CSV export, msgpack row streams, document export). Forces sign-out on
// a 401 and always throws a typed APIError — mirrors api()'s 4xx handling so the
// auth-failure rule lives in one place.
const throwFromStreamResponse = async (response: Response, endpoint: string): Promise<never> => {
  let errorJson: ResponseError
  try {
    errorJson = (await response.json()) as ResponseError
  } catch {
    if (response.status === 401) {
      handleTokenExpired({ endpoint, method: 'GET' })
    }
    throw new APIError(ApiErrorCodes.UNKNOWN, response.status, SERVER_ERROR_MESSAGE, undefined, endpoint, 'GET')
  }
  if (response.status === 401 || errorJson.code === ApiErrorCodes.UNAUTHORIZED) {
    handleTokenExpired({ endpoint, method: 'GET' })
  }
  throw new APIError(
    errorJson.code,
    response.status,
    errorJson.detail.message,
    errorJson.detail.errors,
    endpoint,
    'GET',
    undefined,
    errorJson.needsSupport
  )
}

export default async function api(params: ApiParams, maxRetries = 3): Promise<any> {
  if (!isPublicEndpoint(params.endpoint) && isSessionExpired()) {
    handleTokenExpired(params)
  }

  const RATE_IGNORE_ENDPOINTS = [
    '/allowed-methods',
    '/authenticate',
    '/send-password-reset-email',
    '/invite-multiple',
    '/send-verification-email'
  ]
  const isRateIgnored = RATE_IGNORE_ENDPOINTS.some((ignored) => params.endpoint?.includes(ignored))

  // All retry / backoff / auth-failure handling lives in helpers/apiRetry so it
  // is unit-tested in one place. The 429 path is bounded there — the legacy loop
  // retried 429s forever, which turned a transient rate-limit into a storm.
  return requestWithRetry(() => sendRequest(params), {
    endpoint: params.endpoint,
    method: params.method || 'GET',
    isPublic: isPublicEndpoint(params.endpoint),
    isRateIgnored,
    maxRetries,
    onUnauthorized: () => handleTokenExpired(params)
  })
}

api.createProject = async (name: string, site?: string): Promise<ResponsePayload<IProject>> => {
  const data: Record<string, unknown> = { name }
  // Only send a site when one was selected - a null site is rejected by the API
  // for users whose organisation has sites.
  if (site) data.site = site

  return api({
    method: 'POST',
    endpoint: '/hub',
    data
  })
}

interface UpdateProjectParams {
  publicId: string
  name?: string
  defaultProcessId?: string
  allowTableExport?: boolean
  allowDocumentExport?: boolean
  public?: boolean
  domainsAccess?: string[]
  mfaRequired?: boolean
  hideProcessCreated?: boolean
  site?: Pick<ISite, 'publicId'>
  primaryColour?: string
  fontColour?: string
  defaultBanner?: string | null
  wordTemplate?: string | null
  wordTableStyle?: string | null
  defaultDateFormat?: string
  defaultDatetimeFormat?: string
  defaultHeaderBackgroundColor?: string
  defaultHeaderTextColor?: string
  processTitleFontSize?: number
  processTitleBold?: boolean
  processTitleItalic?: boolean
  processTitleUnderline?: boolean
  processTitleColour?: string
  processTitleAlignment?: 'left' | 'center' | 'right'
}

// The fields sent as they are, when present: those above with their own rules aside.
const UPDATE_PROJECT_FIELDS = [
  'primaryColour',
  'fontColour',
  'defaultBanner',
  'wordTemplate',
  'wordTableStyle',
  'defaultDateFormat',
  'defaultDatetimeFormat',
  'defaultHeaderBackgroundColor',
  'defaultHeaderTextColor',
  'processTitleFontSize',
  'processTitleBold',
  'processTitleItalic',
  'processTitleUnderline',
  'processTitleColour',
  'processTitleAlignment'
] as const

/** Replaces text in a hub's document names, section titles and section content (not table content). */
api.bulkReplaceDocumentText = async (projectId: string, searchText: string, replaceText: string) => {
  return api({
    method: 'PUT',
    endpoint: `/hub/${projectId}`,
    data: { bulkUpdateText: { searchText, replaceText } }
  })
}

api.updateProject = async (params: UpdateProjectParams) => {
  const data: Record<string, unknown> = {}
  if ('name' in params) data.name = params.name
  if ('defaultProcessId' in params) data.defaultProcessId = params.defaultProcessId
  if ('allowTableExport' in params) data.allowTableExport = params.allowTableExport
  if ('allowDocumentExport' in params) data.allowDocumentExport = params.allowDocumentExport
  if ('public' in params) data.public = params.public
  if ('domainsAccess' in params) data.domainsAccess = params.domainsAccess
  if ('mfaRequired' in params) data.mfaRequired = params.mfaRequired
  if ('hideProcessCreated' in params) data.hideProcessCreated = params.hideProcessCreated
  // A hub cannot be detached from its site, so never send a null site.
  if (params.site?.publicId) data.site = params.site.publicId
  for (const field of UPDATE_PROJECT_FIELDS) {
    if (field in params) data[field] = params[field]
  }

  return api({
    method: 'PUT',
    endpoint: `/hub/${params.publicId}`,
    data
  })
}

interface SearchProjectParams {
  search: string
  projectId: string
  processPublicId?: string
}

api.searchProjectResources = async (params: SearchProjectParams) => {
  const projectId = params['projectId']
  const paramsToSend: { search: string; projectId?: string; processPublicId?: string } = { ...params }
  delete paramsToSend.projectId
  return api({ method: 'GET', endpoint: `/hub/${projectId}/search-resources`, data: paramsToSend })
}

api.deleteProject = async (projectId: string) => {
  return api({ method: 'DELETE', endpoint: `/hub/${projectId}` })
}

api.permanentlyDeleteProject = async (projectId: string) => {
  return api({ method: 'DELETE', endpoint: `/hub/${projectId}/permanent` })
}

interface RestoreProjectParams {
  projectId: string
}

api.restoreProject = async (params: RestoreProjectParams) => {
  const projectId = params['projectId']
  const paramsToSend: { projectId?: string } = { ...params }
  delete paramsToSend.projectId
  return api({ method: 'PUT', endpoint: `/hub/${projectId}/restore` })
}

api.duplicateProject = async (
  params: Omit<DuplicateParams, 'targetProjectId' | 'duplicateLinkedTables'>
): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/hub/${params.publicId}/duplicate`,
    data: {
      duplicatePermissions: params.duplicatePermissions,
      lockResource: params.lockResource
    }
  })
}

api.getProjectDeletedProcesses = async (projectId: string) => {
  return api({ method: 'GET', endpoint: `/hub/${projectId}/deleted-documents`, data: { context: { projectId } } })
}

api.getProjectDeletedTables = async (projectId: string) => {
  return api({ method: 'GET', endpoint: `/hub/${projectId}/deleted-tables` })
}

interface InviteUserParams {
  emails: string[]
  projectId: string
  tags?: string[]
  projectRole?: string
}

api.createInvite = async (params: InviteUserParams): Promise<string> => {
  return api({
    method: 'POST',
    endpoint: `/hub/${params.projectId}/invite-multiple`,
    data: {
      emails: params.emails,
      tags: params.tags ? params.tags : undefined,
      projectRole: params.projectRole ? params.projectRole : undefined
    }
  })
}

api.updateInvite = async (
  invite: IMemberInvite,
  params: Pick<InviteUserParams, 'tags' | 'projectId' | 'projectRole'>
): Promise<IMemberInvite> => {
  return api({
    method: 'PUT',
    endpoint: `/hub/${params.projectId}/invite/${invite.publicId}`,
    data: {
      tags: params.tags,
      projectRole: params.projectRole
    }
  })
}

interface NewTableParams extends IContext {
  name: string
  columns?: { name: string; width?: number; kind?: string }[]
  projectId?: string
  /** The sidebar folder to create it in; omitted or null for the top level. */
  folderId?: string | null
}

api.createNewTable = async (params: NewTableParams): Promise<ResponsePayload<ITable>> => {
  if (!params.columns || params.columns.length === 0) {
    params.columns = []
    params.columns.push({ name: 'Column 1', kind: 'text' })
  }
  return api({ method: 'POST', endpoint: '/table', data: params })
}

api.createNewView = async (tableId: string, viewData: INewView): Promise<ResponsePayload<ITableViewWithColumns>> => {
  return api({
    method: 'POST',
    endpoint: `/table/${tableId}/views`,
    data: viewData
  })
}

interface UpdateTableParams extends IContext {
  name: string
  variables?: string[]
  keepValidationsInSync?: boolean
  keepColoursInSync?: boolean
  syncHourlyFrequency?: number
  allowComments?: boolean
}

api.updateTable = async (
  tableId: string,
  params: UpdateTableParams,
  headers?: Headers
): Promise<ResponsePayload<ITable>> => {
  return api({ method: 'PUT', endpoint: `/table/${tableId}`, data: params, headers })
}

api.updateTableView = async (
  viewId: string,
  projectId: string,
  field:
    | 'name'
    | 'description'
    | 'type'
    | 'disableNewRow'
    | 'disableSyncCsv'
    | 'allowContributorDelete'
    | 'displayValidationErrorRows'
    | 'displayCommentRows'
    | 'collapsedGroupView'
    | 'unpackMultiselectGroupView',
  value: string | EditorContent | null | number | boolean
) => {
  return api({
    method: 'PUT',
    endpoint: `/table/views/${viewId}`,
    data: { [field]: value, context: { projectId: projectId } }
  })
}

api.updateWholeTableView = async (
  viewId: string,
  newTableView: Partial<ITableViewWithColumns> & IContext
): Promise<ResponsePayload<ITableViewWithColumns>> => {
  return api({ method: 'PUT', endpoint: `/table/views/${viewId}`, data: newTableView })
}

api.discardCustomViewDraft = async (viewId: string): Promise<ResponsePayload<ITableViewWithColumns>> => {
  return api({ method: 'DELETE', endpoint: `/table/views/${viewId}/custom-view-draft` })
}

api.deleteView = async (viewId: string, projectId: string) => {
  return api({
    method: 'DELETE',
    endpoint: `/table/views/${viewId}`,
    data: { context: { projectId } }
  })
}

api.getTableJoins = async (tableId: string): Promise<ResponsePayload<ITableJoin[]>> => {
  return api({ method: 'GET', endpoint: `/table/${tableId}/joins` })
}

api.createTableJoin = async (
  tableId: string,
  projectId: string,
  tableJoin: ICreateTableJoin
): Promise<ResponsePayload<ITable>> => {
  return api({
    method: 'POST',
    endpoint: `/table/${tableId}/join`,
    data: { ...tableJoin, context: { projectId } }
  })
}

api.updateTableJoin = async (
  tableId: string,
  joinId: string,
  projectId: string,
  tableJoin: ICreateTableJoin
): Promise<ResponsePayload<ITable>> => {
  return api({
    method: 'PUT',
    endpoint: `/table/${tableId}/join/${joinId}`,
    data: { ...tableJoin, context: { projectId } }
  })
}

api.deleteTableJoin = async (tableId: string, joinId: string, projectId: string) => {
  return api({
    method: 'DELETE',
    endpoint: `/table/${tableId}/join/${joinId}`,
    data: { context: { projectId } }
  })
}

api.createTableColumn = async (
  viewId: string,
  column: ICreateTableColumnView
): Promise<ResponsePayload<ITableViewColumn>> => {
  return api({
    method: 'POST',
    endpoint: `/table/views/${viewId}/columns`,
    data: column
  })
}

api.updateTableColumn = async (
  viewId: string,
  columnId: string,
  column: Partial<ITableViewColumn> & IContext & Partial<{ alterOptions: IColumnAlterOptions }>
): Promise<ResponsePayload<ITableViewColumn>> => {
  return api({
    method: 'PUT',
    endpoint: `/table/views/${viewId}/columns/${columnId}`,
    data: column
  })
}

api.deleteTableColumn = async (
  tableId: string,
  columnId: string,
  projectId: string
): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/table/${tableId}/column/${columnId}`,
    data: { context: { projectId } }
  })
}

api.deleteTableRows = async (
  viewId: string,
  rowIds: string[],
  projectId: string,
  processPublicId?: string,
  processSectionPublicId?: string
): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/table/views/${viewId}/rows`,
    data: {
      rowIds: rowIds,
      context: {
        projectId: projectId,
        processPublicId: processPublicId,
        processSectionPublicId: processSectionPublicId
      }
    }
  })
}

api.truncateTable = async (tableId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/table/${tableId}/truncate`
  })
}

api.updateTableRows = async (
  viewId: string,
  rows: Array<{ publicId: string; rowData: Record<string, unknown> }>,
  projectId: string
): Promise<ResponsePayload<ITableRow[]>> => {
  return api({
    method: 'PUT',
    endpoint: `/table/views/${viewId}/rows`,
    data: {
      rows,
      context: { projectId }
    }
  })
}

interface GetFileParams {
  tableId: string
  columnId: string
  filename: string
}
api.getFile = ({ tableId, columnId, filename }: GetFileParams) => {
  const url =
    `/table/${tableId}/file` +
    encodeURIComponent(
      JSON.stringify({
        columnId,
        filename
      })
    )

  return downloadFileToClient(url, filename)
}

export interface IGetTableViewQueryParams {
  ignoreCachedOptions?: boolean
}

api.getTableView = async (
  tableViewId: string,
  params?: IGetTableViewQueryParams
): Promise<ResponsePayload<ITableViewWithColumns>> => {
  const queryString = params ? objectUrlQueryfy(params!) : ''
  return api({ method: 'GET', endpoint: `/table/views/${tableViewId}${queryString}` })
}

export interface IGetTableViewsQueryParams {
  ignoreColumns?: boolean
  onlyPersonalViews?: boolean
  excludePersonalViews?: boolean
}

api.getTableViews = async (
  tableId: string,
  params?: IGetTableViewsQueryParams
): Promise<ResponsePayload<ITableViewWithColumns[]>> => {
  const queryString = params ? objectUrlQueryfy(params!) : ''
  return api({ method: 'GET', endpoint: `/table/${tableId}/views${queryString}` })
}

// Downloads the view as CSV or Excel; the body says which, and carries the live filters,
// sort, hidden columns and colour rules (see buildExportRequestBody).
api.exportTableView = async (viewId: string, filename: string, body: IExportTableViewBody) => {
  return downloadFileToClient(`/table/views/${viewId}/export`, filename, { method: 'POST', body })
}

api.getTableViewCSVAsText = async (viewId: string): Promise<string> => {
  const url = `${apiPrefix}/table/views/${viewId}/csv`
  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: getAuthToken()!
    }
  })
  if (!response.ok) {
    await throwFromStreamResponse(response, url)
  }
  return response.text()
}

api.getTableComments = async (tableId: string): Promise<ResponsePayload<ICommentThreadStats[]>> => {
  return api({ method: 'GET', endpoint: `/comment_thread/stats?referenceType=table&mainReferenceId=${tableId}` })
}

interface IAsyncRowsReturn {
  type: 'header' | 'kinds' | 'meta' | 'rows'
  rows?: ITableRowCompressed[]
  // Set instead of `rows` when the server streams the `columnar` format.
  columnar?: IColumnarBatch
  header?: Array<string>
  kinds?: Array<string>
  dictUpdates?: Record<number, unknown[]>
  // Only which rows are locked is streamed — never the hidden values or rules.
  lockedRowIndices?: number[]
  total?: number
  batchSize?: number
}

api.getTableViewRows = async (tableViewId: string, rowIds?: string[]): Promise<ResponsePayload<ITableRow[]>> => {
  return api({ method: 'POST', endpoint: `/table/views/${tableViewId}/get-rows`, data: { rowIds } })
}

api.getTableViewRowsAsync = async function* (
  tableId: string,
  tableViewId: string,
  isAdmin: boolean,
  processId?: string,
  signal?: AbortSignal
): AsyncGenerator<IAsyncRowsReturn> {
  const path = isAdmin ? `/table/${tableId}/rows-stream` : `/table/views/${tableViewId}/rows-stream`
  // Ask for the columnar wire format; the meta frame says which format the server actually sent.
  const streamUri = (columnar: boolean): string => {
    const query: string[] = []
    if (columnar) query.push('format=columnar')
    if (processId !== undefined) query.push(`processId=${processId}`)
    return query.length > 0 ? `${path}?${query.join('&')}` : path
  }
  const request = (uri: string): Promise<Response> =>
    fetch(`${apiPrefix}${uri}`, {
      method: 'GET',
      headers: {
        Authorization: getAuthToken()!
      },
      cache: 'no-store',
      signal
    })

  let uri = streamUri(true)
  let response = await request(uri)
  // An API from before the columnar format rejects the unknown `format` parameter with a 422. That
  // can happen while a release rolls out, when a new webapp pod reaches an old API pod: fall back
  // to the legacy row format, which the decoding below still reads.
  if (response.status === 422) {
    uri = streamUri(false)
    response = await request(uri)
  }

  if (!response.ok) {
    await throwFromStreamResponse(response, uri)
  }

  if (response.body === null) {
    return
  }

  let gotHeader = false
  let gotKinds = false
  let gotMeta = false
  let columnar = false
  let ended = false
  for await (const data of decodeMultiStream(response.body)) {
    if (ended) {
      throw new Error('The row stream continued after its end marker')
    }
    if (gotMeta && columnar && !Array.isArray(data)) {
      // The columnar stream's last frame: everything the server meant to send has arrived.
      ended = true
    } else if (!gotHeader) {
      gotHeader = true
      yield { type: 'header', header: data as string[] }
    } else if (!gotKinds) {
      gotKinds = true
      yield { type: 'kinds', kinds: data as string[] }
    } else if (!gotMeta) {
      gotMeta = true
      const meta = data as { total: number; batchSize: number; format?: string }
      columnar = meta.format === 'columnar'
      yield { type: 'meta', total: meta.total, batchSize: meta.batchSize }
    } else if (columnar) {
      const [dictUpdates, rowCount, publicIds, sortOrders, columns, lockedRowIndices] = data as [
        Record<number, unknown[]>,
        number,
        Uint8Array,
        Uint8Array,
        IColumnarColumnPayload[],
        number[]?
      ]
      yield { type: 'rows', columnar: { rowCount, publicIds, sortOrders, columns }, dictUpdates, lockedRowIndices }
    } else {
      const [dictUpdates, rows, lockedRowIndices] = data as [
        Record<number, unknown[]>,
        ITableRowCompressed[],
        number[]?
      ]
      yield { type: 'rows', rows, dictUpdates, lockedRowIndices }
    }
  }
  // A body that ends cleanly at a frame boundary before the marker was cut short (a proxy or the
  // network gave up); the consumer treats this like any other failed load rather than as complete.
  if (columnar && !ended) {
    throw new Error('The row stream ended before the server had sent every row')
  }
}

api.createNewRow = (
  projectId: string,
  tableViewId: string,
  rowData: Record<string, unknown> = {},
  nextRowPublicId?: string,
  context?: SpreadsheetInProcessContext
): Promise<ResponsePayload<ITableRow[]>> => {
  const payload: { rowData: Record<string, unknown>; nextRowPublicId?: string } = {
    rowData
  }

  const processContext = context
    ? {
        projectId: projectId,
        processPublicId: context.processId,
        processSectionPublicId: context.processSectionId
      }
    : { projectId: projectId }

  return api({
    method: 'POST',
    endpoint: `/table/views/${tableViewId}/rows`,
    data: { rows: [payload], nextRowPublicId, context: processContext }
  })
}

api.createNewRows = (
  projectId: string,
  tableViewId: string,
  rows: Array<{ rowData: Record<string, unknown>; sortOrder?: number }>,
  context?: SpreadsheetInProcessContext
): Promise<ResponsePayload<ITableRow[]>> => {
  const processContext = context
    ? {
        projectId: projectId,
        processPublicId: context.processId,
        processSectionPublicId: context.processSectionId
      }
    : { projectId: projectId }
  return api({ method: 'POST', endpoint: `/table/views/${tableViewId}/rows`, data: { rows, context: processContext } })
}

api.createBlankRows = (
  projectId: string,
  tableViewId: string,
  numberRows: number,
  context?: SpreadsheetInProcessContext,
  values?: Record<string, ICellValue>
): Promise<ResponsePayload<ITableRow[]>> => {
  const processContext = context
    ? {
        projectId: projectId,
        processPublicId: context.processId,
        processSectionPublicId: context.processSectionId
      }
    : { projectId: projectId }

  const rows = []
  for (let i = 0; i < numberRows; i++) {
    if (values) {
      rows.push({ rowData: { ...values } })
    } else {
      rows.push({ rowData: {} })
    }
  }

  return api({ method: 'POST', endpoint: `/table/views/${tableViewId}/rows`, data: { rows, context: processContext } })
}

api.previewRow = (
  projectId: string,
  tableViewId: string,
  rowData: Record<string, unknown> = {},
  signal: AbortSignal,
  context?: SpreadsheetInProcessContext
): Promise<ResponsePayload<Record<string, ICellValue>>> => {
  const processContext = context
    ? {
        projectId: projectId,
        processPublicId: context.processId,
        processSectionPublicId: context.processSectionId
      }
    : { projectId: projectId }

  return post(`/table/views/${tableViewId}/preview-row`, { rowData, context: processContext }, signal)
}

api.getChartData = async (tableViewId: string, params?: any): Promise<ResponsePayload<Record<string, number>>> => {
  return api({
    method: 'GET',
    endpoint: `/table/views/${tableViewId}/chart${params ? '?' + new URLSearchParams(params) : ''}`
  })
}

api.requestCsvBackup = async (tableId: string, date: string) => {
  return api({
    method: 'GET',
    endpoint: `/table/${tableId}/csv-backup?date=${date}`
  })
}

api.getTableViewDistinct = async (
  viewId: string,
  columnId: string,
  filters?: Array<any>
): Promise<ResponsePayload<string[]>> => {
  const query = new URLSearchParams()
  if (filters?.length) {
    convertToApiFilter(filters).forEach((filter) => query.append('filter', JSON.stringify(filter)))
  }
  const qs = query.toString()
  return api({
    method: 'GET',
    endpoint: `/table/views/${viewId}/column/${columnId}/distinct${qs ? `?${qs}` : ''}`
  })
}

api.getUser = async (firebaseId: string): Promise<ResponsePayload<IUserObject>> => {
  return api({ method: 'GET', endpoint: `/user/${firebaseId}` })
}

api.getUserByPublicId = async (publicId: string): Promise<ResponsePayload<IUserObject>> => {
  return api({ method: 'GET', endpoint: `/user/public/${publicId}` })
}

api.getMe = async (): Promise<ResponsePayload<IUserObject>> => {
  return api({ method: 'GET', endpoint: `/user/me` })
}

export interface NewUserParams {
  name: string
  email: string
  password: string
  template?: string
}

api.createUser = async (params: NewUserParams) => {
  return api({ method: 'POST', endpoint: '/user', data: params })
}

export interface UpdateUserProfileParams {
  name: string
  bio: string
  linkedin: string
  twitter: string
  university: string
  universityDegree: string
  profilePicture: string
  organisation: string
  location: string
  website: string
}

api.updateUserProfile = async (params: UpdateUserProfileParams): Promise<ResponsePayload<IUserObject>> => {
  return api({ method: 'PUT', endpoint: '/user', data: params })
}

api.getBilling = async () => {
  return api({ method: 'GET', endpoint: '/user/billing' })
}

interface DeleteResourceParams extends IContext {
  projectId?: string
}

api.deleteProcess = async (processId: string, params: DeleteResourceParams, headers?: Headers) => {
  return api({ method: 'DELETE', endpoint: `/document/${processId}`, data: params, headers })
}

api.syncTemplate = async (processId: string) => {
  return api({ method: 'GET', endpoint: `/document/${processId}/sync-template` })
}

api.deleteTable = async (tableId: string, params: DeleteResourceParams, headers?: Headers) => {
  return api({ method: 'DELETE', endpoint: `/table/${tableId}`, data: params, headers })
}

interface RestoreProcessParams extends IContext {
  projectId?: string
}

api.restoreProcess = async (processId: string, params: RestoreProcessParams) => {
  return api({ method: 'PUT', endpoint: `/document/${processId}/restore`, data: params })
}

interface RestoreTableParams extends IContext {
  projectId?: string
}

api.getTable = async (tableId: string, ignoreCachedOptions?: boolean): Promise<ResponsePayload<ITable>> => {
  return api({ method: 'GET', endpoint: `/table/${tableId}?ignoreCachedOptions=${ignoreCachedOptions}` })
}

api.restoreTable = async (tableId: string, params: RestoreTableParams) => {
  return api({ method: 'PUT', endpoint: `/table/${tableId}/restore`, data: params })
}

interface NewProcessParams extends IContext {
  name: string
  projectId?: string
  /** The sidebar folder to create it in; omitted or null for the top level. */
  folderId?: string | null
}
api.createNewProcess = async (params: NewProcessParams) => {
  return api({ method: 'POST', endpoint: '/process', data: params })
}

api.createNewProcessFromFile = async (projectId: string, name: string, file: File, folderId?: string | null) => {
  const token = getAuthToken()

  return new Promise(function (resolve, reject) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('name', name)

    if (projectId) {
      formData.append('projectId', projectId)
    }
    if (folderId) {
      formData.append('folderId', folderId)
    }

    const xhr = new XMLHttpRequest()
    // Add any event handlers here...
    xhr.open('POST', `${apiPrefix}/document/import`, true)
    xhr.setRequestHeader('Authorization', token!)

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4 && xhr.status !== 0) {
        if (xhr.status === 200) {
          resolve(xhr.response)
        } else {
          try {
            const json = JSON.parse(xhr.response)
            reject(
              new APIError(
                json.code || (xhr.status.toString() as ApiErrorCodes),
                xhr.status,
                json.detail.message,
                json.detail.errors,
                '/document/import',
                'POST',
                json.links,
                json.needsSupport
              )
            )
          } catch {
            reject(
              new APIError(
                xhr.status.toString() as ApiErrorCodes,
                xhr.status,
                "The document couldn't be imported. Check the file is a Word (.docx) document and try again.",
                [],
                '/document/import',
                'POST'
              )
            )
          }
        }
      }
    }
    xhr.onerror = () => {
      reject(
        new APIError(
          ApiErrorCodes.UNKNOWN,
          0,
          "The document couldn't be uploaded. Check your connection and try again.",
          [],
          '/document/import',
          'POST'
        )
      )
    }
    xhr.send(formData)
  })
}

interface DuplicateParams {
  publicId: string
  targetProjectId: string
  duplicatePermissions?: boolean
  duplicateTables?: boolean
  lockResource?: boolean
  /** A sidebar folder in the target hub to file the copy in. Omitted: where the server puts it by default. */
  folderId?: string | null
}

api.duplicateTable = async (params: DuplicateParams): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/table/${params.publicId}/duplicate`,
    data: {
      targetProjectId: params.targetProjectId,
      duplicateLinkedTables: params.duplicateTables === undefined ? undefined : params.duplicateTables,
      duplicatePermissions: params.duplicatePermissions
    }
  })
}

api.duplicateProcess = async (params: DuplicateParams): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/document/${params.publicId}/duplicate`,
    data: {
      targetProjectId: params.targetProjectId,
      duplicateLinkedTables: params.duplicateTables === undefined ? undefined : params.duplicateTables,
      duplicatePermissions: params.duplicatePermissions === undefined ? undefined : params.duplicatePermissions,
      lockResource: params.lockResource,
      folderId: params.folderId
    }
  })
}

api.duplicateView = async (
  tableId: string,
  params: Omit<DuplicateParams, 'targetProjectId' | 'duplicateLinkedTables'>
): Promise<ResponsePayload<ITableViewWithColumns>> => {
  return api({
    method: 'POST',
    endpoint: `/table/${tableId}/views/${params.publicId}/duplicate`,
    data: {
      duplicatePermissions: true,
      lockResource: params.lockResource
    }
  })
}

interface UpdateProcessParams extends IContext {
  processId: string
  name?: string
  description?: any
  logo?: string | null
  permissions?: Array<Array<string>>
  primaryColour?: string
  secondaryColour?: string
  variables?: string[]
  bannerHeight?: number | null
  expandByDefault?: boolean
  allowComments?: boolean
  isTemplate?: boolean
  lockedTemplate?: boolean
}

api.updateProcess = async (params: UpdateProcessParams, headers?: Headers) => {
  const processId = params['processId']
  const paramsToSend: {
    name?: string
    description?: any
    expandByDefault?: boolean
    allowComments?: boolean
    processId?: string
    logo?: string | null
    permissions?: Array<Array<string>>
    variables?: string[]
    bannerHeight?: number | null
    isTemplate?: boolean
    lockedTemplate?: boolean
  } = { ...params }
  delete paramsToSend.processId
  return api({ method: 'PUT', endpoint: `/document/${processId}`, data: paramsToSend, headers })
}

interface GetProcessParams {
  excludeChildren?: boolean
}

api.getProcess = async (uuid: string, params: GetProcessParams): Promise<ResponsePayload<IProcessObject>> => {
  return api({ method: 'GET', endpoint: '/document/' + uuid, data: params })
}

api.getProcessAnalytics = async (uuid: string) => {
  return api({ method: 'GET', endpoint: '/document/' + uuid + '/analytics' })
}

api.getDeletedProcessSections = async (processId: string) => {
  return api({ method: 'GET', endpoint: `/document/${processId}/deletedsections` })
}

interface NewProcessSectionParams extends IContext {
  name: string
  plaintextDescription?: string
  processId: string
  parentId: string | null
}

api.createNewProcessSection = async (params: NewProcessSectionParams) => {
  const processId = params['processId']
  const paramsToSend: {
    name: string
    plaintextDescription?: string
    parentId: string | null
    processId?: string
  } = { ...params }
  delete paramsToSend.processId
  return api({ method: 'POST', endpoint: `/document/${processId}/section`, data: paramsToSend })
}

interface GetProcessSectionParams {
  mainParentSection?: boolean
}

api.getProcessSection = async (
  processId: string,
  processSectionId: string,
  params: GetProcessSectionParams
): Promise<ResponsePayload<IProcessSectionObject>> => {
  return api({
    method: 'GET',
    endpoint: '/document/' + processId + '/section/' + processSectionId,
    data: params
  })
}

interface UpdateProcessSectionParams extends IContext {
  name: string
  description: any
  processId: string
  processSectionId: string
  pdfIncludeDescription?: boolean
  pdfIncludeSection?: boolean
  pageBreakBefore?: boolean
}

api.updateProcessSection = async (params: UpdateProcessSectionParams) => {
  const processId = params['processId']
  const processSectionId = params['processSectionId']
  const paramsToSend: {
    name: string
    description: any
    processId?: string
    processSectionId?: string
    pdfIncludeDescription?: boolean
    pdfIncludeSection?: boolean
    pageBreakBefore?: boolean
  } = { ...params }
  delete paramsToSend.processId
  delete paramsToSend.processSectionId

  return api({
    method: 'PUT',
    endpoint: `/document/${processId}/section/${processSectionId}?${WEB_CLIENT_QUERY}`,
    data: paramsToSend
  })
}

interface UpdateProcessSectionOrderParams extends IContext {
  processSections: Array<any>
  processId: string
}

api.updateProcessSectionOrder = async (params: UpdateProcessSectionOrderParams) => {
  const processId = params['processId']
  const paramsToSend: {
    processSections: Array<any>
    processId?: string
  } = { ...params }
  delete paramsToSend.processId

  return api({ method: 'PUT', endpoint: `/document/${processId}/changesectionorder`, data: paramsToSend })
}

api.deleteProcessSection = async (projectId: string, processId: string, processSectionId: string) => {
  return api({
    method: 'DELETE',
    endpoint: `/document/${processId}/section/${processSectionId}`,
    data: { context: { projectId } }
  })
}

api.restoreProcessSection = async (projectId: string, processId: string, processSectionId: string) => {
  return api({
    method: 'PUT',
    endpoint: `/document/${processId}/section/${processSectionId}/restore`,
    data: { context: { projectId } }
  })
}

api.duplicateProcessSection = async (
  projectId: string,
  processId: string,
  processSectionId: string
): Promise<ResponsePayload<IProcessSectionObject>> => {
  return api({
    method: 'POST',
    endpoint: `/document/${processId}/section/${processSectionId}/duplicate`,
    data: { context: { projectId } }
  })
}

type EmbedSubmissionSettings = { enableSubmission: boolean; resetAfterSubmission: boolean }

api.createEmbedSubmission = async (
  projectId: string,
  processId: string,
  processSectionId: string,
  settings: EmbedSubmissionSettings
) =>
  api({
    method: 'POST',
    endpoint: `/document/${processId}/section/${processSectionId}/submission`,
    data: { ...settings, context: { projectId } }
  })

api.updateEmbedSubmission = async (
  projectId: string,
  processId: string,
  processSectionId: string,
  submissionId: string,
  settings: Partial<EmbedSubmissionSettings>
) =>
  api({
    method: 'PUT',
    endpoint: `/document/${processId}/section/${processSectionId}/submission/${submissionId}`,
    data: { ...settings, context: { projectId } }
  })

api.submitEmbedSubmission = async (
  projectId: string,
  processId: string,
  processSectionId: string,
  submissionId: string
) =>
  api({
    method: 'PUT',
    endpoint: `/document/${processId}/section/${processSectionId}/submission/${submissionId}/submit`,
    data: { context: { projectId } }
  })

api.resetEmbedSubmission = async (
  projectId: string,
  processId: string,
  processSectionId: string,
  submissionId: string
) =>
  api({
    method: 'PUT',
    endpoint: `/document/${processId}/section/${processSectionId}/submission/${submissionId}/reset`,
    data: { context: { projectId } }
  })

api.exportDocument = async (
  processId: string,
  tableLinks: boolean,
  iframeLinks: boolean,
  pageFormat: string,
  pageOrientation: string,
  defaultFormatAndOrientation: boolean,
  viewGrids?: ViewGrids
): Promise<ArrayBuffer | null> => {
  // POST (not GET) so the optional matrix/transposed view layouts can be carried in the body —
  // these can be far larger than a query string allows. The backend formats the referenced cell
  // values; the frontend only supplies the pivot arrangement.
  const url = `/document/${processId}/export`
  const response = await fetch(`${apiPrefix}${url}`, {
    method: 'POST',
    headers: {
      Authorization: getAuthToken()!,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      tableLinks,
      iframeLinks,
      pageFormat,
      pageOrientation,
      setDefaultFormatAndOrientation: defaultFormatAndOrientation,
      viewGrids: viewGrids ?? {}
    })
  })

  if (!response.ok) {
    await throwFromStreamResponse(response, url)
  }

  return await response.arrayBuffer()
}

api.createNotification = async (projectId: string, values: any): Promise<ResponsePayload<INotification>> => {
  return api({
    method: 'POST',
    endpoint: `/notifications`,
    data: {
      projectId: projectId,
      ...values
    }
  })
}

api.updateNotification = async (notification: INotification, values: any): Promise<ResponsePayload<INotification>> => {
  return api({
    method: 'PUT',
    endpoint: `/notifications/${notification.publicId}`,
    data: values
  })
}

api.getUserNotifications = async (
  page?: number,
  unreadOnly?: boolean,
  size?: number,
  nextPageToken?: string
): Promise<{ data: IUserNotification[]; metadata: IUserNotificationListMetadata }> => {
  return api({
    method: 'GET',
    endpoint: `/user/notifications?page=${page ?? 1}${unreadOnly ? '&unreadOnly=true' : ''}${
      size ? `&size=${size}` : ''
    }${nextPageToken ? `&nextPageToken=${encodeURIComponent(nextPageToken)}` : ''}`
  })
}

api.updateUserNotification = async (
  notificationId: string,
  values: { read?: boolean; dismissed?: boolean }
): Promise<ResponsePayload<IUserNotification>> => {
  return api({
    method: 'PATCH',
    endpoint: `/user/notifications/${notificationId}`,
    data: values
  })
}

api.markAllNotificationsRead = async (): Promise<ResponsePayload<{ updated: number }>> => {
  return api({
    method: 'POST',
    endpoint: `/user/notifications/read-all`
  })
}

api.updateEmailPreferences = async (preferences: EmailPreferences): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'PUT',
    endpoint: `/user/account`,
    data: preferences
  })
}

api.getEvents = async (
  resourcePublicId: string,
  type: string,
  page: number,
  users: string[],
  verb: string[],
  startDate: string | null,
  endDate: string | null,
  search: string | null
): Promise<ResponsePayload<ILogObject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/notifications/events/${resourcePublicId}`,

    data:
      users.length > 0
        ? { type, page, users, verb, startDate, endDate, search }
        : { type, page, verb, startDate, endDate, search }
  })
}

api.getAdminEvents = async (
  page: number,
  users: string[],
  email: string,
  verb: string[],
  startDate: string | null,
  endDate: string | null,
  search: string | null,
  projects: string[]
): Promise<ResponsePayload<ILogObject[]>> => {
  let data: {
    page: number
    verb: string[]
    email: string
    startDate: string | null
    endDate: string | null
    search: string | null
    users?: string[]
    projects?: string[]
  } = { page, verb, email, startDate, endDate, search }
  if (users.length > 0) {
    data = { ...data, users }
  }
  if (projects.length > 0) {
    data = { ...data, projects }
  }
  return api({
    method: 'GET',
    endpoint: `/admin/events`,
    data
  })
}

api.getNotifications = async (projectId: string): Promise<ResponsePayload<INotification[]>> => {
  return api({ method: 'GET', endpoint: `/hub/${projectId}/notifications` })
}

api.deleteNotification = async (publicId: string) => {
  return api({
    method: 'DELETE',
    endpoint: `/notifications/${publicId}`
  })
}

api.getNotificationEventTypes = async (): Promise<ResponsePayload<string[]>> => {
  return api({
    method: 'GET',
    endpoint: `/notifications/event-types`
  })
}

interface IProjectResourcesCall {
  projectPermissions?: boolean
  excludeProcesses?: boolean
  excludeTables?: boolean
  onlyDeleted?: boolean
  onlyAdmin?: boolean
}

api.getProject = async (projectId: string): Promise<ResponsePayload<IProject>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}?excludeMembers=true`
  })
}

api.getProjectResources = async (
  projectId: string,
  args: IProjectResourcesCall
): Promise<ResponsePayload<IFolderContents[]>> => {
  return api({
    method: 'POST',
    endpoint: `/hub/${projectId}/resources`,
    data: args
  })
}

// The folder writes take optional `headers`: the sidebar sends its tab's origin (helpers/realtimeOrigin).
api.createHubFolder = async (
  projectId: string,
  params: { name: string; parentId: string | null },
  headers?: Headers
): Promise<ResponsePayload<IHubFolder>> => {
  return api({ method: 'POST', endpoint: `/hub/${projectId}/folders`, data: params, headers })
}

api.renameHubFolder = async (
  projectId: string,
  folderId: string,
  name: string,
  headers?: Headers
): Promise<ResponsePayload<IHubFolder>> => {
  return api({ method: 'PUT', endpoint: `/hub/${projectId}/folders/${folderId}`, data: { name }, headers })
}

api.deleteHubFolder = async (
  projectId: string,
  folderId: string,
  headers?: Headers
): Promise<ResponsePayload<object>> => {
  return api({ method: 'DELETE', endpoint: `/hub/${projectId}/folders/${folderId}`, headers })
}

api.moveHubFolderItems = async (
  projectId: string,
  params: { items: ISidebarMoveItem[]; parentFolderId: string | null; index: number | null },
  headers?: Headers
): Promise<ResponsePayload<object>> => {
  return api({ method: 'POST', endpoint: `/hub/${projectId}/folders/move`, data: params, headers })
}

/** The direct children of each container (a folder id, or null for the top level), for a hub Owner's live updates. */
api.getSidebarContainers = async (
  projectId: string,
  containers: (string | null)[]
): Promise<ResponsePayload<ISidebarContainers>> => {
  return api({ method: 'POST', endpoint: `/hub/${projectId}/sidebar/containers`, data: { containers } })
}

api.getProjects = async (includeDeleted?: boolean): Promise<ResponsePayload<IProject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/hubs${includeDeleted ? '?include_deleted=true' : ''}`
  })
}

api.getUserProjectTags = async (projectId: string): Promise<ResponsePayload<ITag[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/hubs/${projectId}/tags`
  })
}

api.getOwnerProjects = async (): Promise<ResponsePayload<IProject[]>> => {
  return api({
    method: 'GET',
    endpoint: '/user/owner-hubs'
  })
}

api.getPublicProjects = async (): Promise<ResponsePayload<IProject[]>> => {
  return api({
    method: 'GET',
    endpoint: '/user/public-hubs'
  })
}

api.getPinnedProjects = async (firebaseUserId: string): Promise<ResponsePayload<IPinnedProjectObject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/${firebaseUserId}/pinned-hubs`
  })
}

api.getAchievements = async (firebaseUserId: string): Promise<ResponsePayload<IAchievementsObject>> => {
  return api({
    method: 'GET',
    endpoint: `/user/${firebaseUserId}/achievements`
  })
}

api.getContributions = async (firebaseUserId: string): Promise<ResponsePayload<Record<string, number>>> => {
  return api({
    method: 'GET',
    endpoint: `/user/${firebaseUserId}/contributions`
  })
}

api.getPublicContributions = async (firebaseUserId: string): Promise<ResponsePayload<ILogObject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/${firebaseUserId}/public-contributions`
  })
}

api.getProjectSecrets = async (projectId: string): Promise<ResponsePayload<IProjectSecret[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/secrets`
  })
}

api.getProjectTags = async (projectId: string): Promise<ResponsePayload<ITableWithCells[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/tags`
  })
}

api.getProjectVariables = async (projectId: string): Promise<ResponsePayload<ITableWithCells[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/variables`
  })
}

api.getProjectTables = async (projectId: string): Promise<ResponsePayload<ITable[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/tables`
  })
}

api.getHubScripts = async (hubId: string): Promise<ResponsePayload<IScriptInventoryRow[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${hubId}/scripts`
  })
}

api.getHubScriptCount = async (hubId: string): Promise<ResponsePayload<{ count: number }>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${hubId}/scripts/count`
  })
}

api.getHubDependencies = async (hubId: string): Promise<ResponsePayload<IDependencyOverview>> => {
  return api({ method: 'GET', endpoint: `/hub/${hubId}/relationships` })
}

api.expandHubDependencyNode = async (
  hubId: string,
  kind: 'table' | 'document',
  publicId: string
): Promise<ResponsePayload<IDependencyExpansion>> => {
  return api({ method: 'GET', endpoint: `/hub/${hubId}/relationships/${kind}/${publicId}` })
}

api.uploadFile = async (file: File, resources: IFileParentResource[]): Promise<ResponsePayload<IFile>> => {
  const body = new FormData()
  body.set('file', file)

  if (resources) {
    body.set('resources', JSON.stringify(resources))
  }

  return api({
    method: 'POST',
    endpoint: `/files`,
    rawBody: true,
    data: body
  })
}

api.uploadFileWithProgress = (
  file: File,
  resources: IFileParentResource[],
  onProgress: (progress: number) => void
): Promise<ResponsePayload<IFile>> => {
  const token = getAuthToken()

  return new Promise((resolve, reject) => {
    const formData = new FormData()
    formData.append('file', file)

    if (resources) {
      formData.append('resources', JSON.stringify(resources))
    }

    const xhr = new XMLHttpRequest()

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const progress = Math.round((event.loaded / event.total) * 100)
        onProgress(progress)
      }
    }

    xhr.open('POST', `${apiPrefix}/files`, true)
    xhr.setRequestHeader('Authorization', token!)

    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText)
            resolve(response)
          } catch {
            reject(new Error('Failed to parse response'))
          }
        } else {
          try {
            const errorResponse = JSON.parse(xhr.responseText)
            reject(
              new APIError(
                errorResponse.code,
                xhr.status,
                errorResponse.detail?.message || errorResponse.message || "Your file couldn't be uploaded. Try again.",
                errorResponse.detail?.errors,
                '/files',
                'POST',
                errorResponse.links,
                errorResponse.needsSupport
              )
            )
          } catch {
            reject(new Error('Upload failed'))
          }
        }
      }
    }

    xhr.onerror = () => {
      reject(new Error('Network error during upload'))
    }

    xhr.send(formData)
  })
}

api.signUrl = async (url: string, signal?: AbortSignal): Promise<ResponsePayload<ISignedUrlToken>> => {
  return api({
    method: 'POST',
    endpoint: '/files/sign',
    data: {
      url
    },
    signal
  })
}

interface CreateCommentThread extends IContext {
  referenceType: CommentReferenceType
  referenceId: string
  commentText: string
  mainReferenceId?: string
}

api.postCommentThread = async (payload: CreateCommentThread): Promise<ResponsePayload<ICommentThreadObject>> => {
  return api({
    method: 'POST',
    endpoint: '/comment_thread',
    data: payload
  })
}

api.updateStatusCommentThread = async (
  context: IContext,
  commentThreadId: string,
  status: CommentThreadStatusType
): Promise<ResponsePayload<ICommentThreadObject>> => {
  return api({
    method: 'PUT',
    endpoint: `/comment_thread/${commentThreadId}/${status}`,
    data: { ...context }
  })
}

api.deleteCommentThread = async (
  context: IContext,
  commentThreadId: string
): Promise<ResponsePayload<ICommentThreadObject>> => {
  return api({
    method: 'DELETE',
    endpoint: `/comment_thread/${commentThreadId}`,
    data: { ...context }
  })
}

api.getCommentThreadsForReference = async (
  referenceType: CommentReferenceType,
  referenceId: string,
  mainReference?: string
): Promise<ResponsePayload<ICommentThreadObject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/comment_thread?referenceType=${referenceType}&referenceId=${referenceId}${
      mainReference ? '&mainReference=' + mainReference : ''
    }`
  })
}

interface CreateCommentProps extends IContext {
  commentText: string
}
api.postComment = async (
  commentThreadId: string,
  data: CreateCommentProps
): Promise<ResponsePayload<ICommentObject>> => {
  return api({
    method: 'POST',
    endpoint: '/comment_thread/' + commentThreadId + '/comment',
    data
  })
}

api.deleteComment = async (
  context: IContext,
  commentThreadId: string,
  commentId: string
): Promise<ResponsePayload<ICommentObject>> => {
  return api({
    method: 'DELETE',
    endpoint: `/comment_thread/${commentThreadId}/comment/${commentId}`,
    data: { ...context }
  })
}

interface UpdateCommentProps extends IContext {
  commentText: string
}

api.updateComment = async (
  commentThreadId: string,
  commentId: string,
  data: UpdateCommentProps
): Promise<ResponsePayload<ICommentObject>> => {
  return api({
    method: 'PUT',
    endpoint: `/comment_thread/${commentThreadId}/comment/${commentId}`,
    data: data
  })
}

api.getResourcePermissions = async (
  resource: string,
  resourceId: string
): Promise<ResponsePayload<IAccessPolicy[]>> => {
  return api({
    method: 'GET',
    endpoint: `/permissions`,
    data: {
      resource,
      resourceId
    }
  })
}

api.getTag = async (tagId: string): Promise<ResponsePayload<ITag>> => {
  return api({
    method: 'GET',
    endpoint: `/permissions/tag?tagId=${tagId}`
  })
}

export interface NewPolicyRequest extends IContext {
  role: UserRoles
  resourceKind: AccessResourceKinds
  resourceId: string
  attributeKind: AccessAttributeKinds
  attributeId?: string
  tagReferenceId?: string
}
api.createAccessPolicy = async (policy: NewPolicyRequest): Promise<ResponsePayload<IAccessPolicy>> => {
  return api({
    method: 'POST',
    endpoint: `/permissions`,
    data: policy
  })
}

api.updateAccessPolicy = async (
  projectId: string,
  policy: IAccessPolicy,
  role: UserRoles
): Promise<ResponsePayload<IAccessPolicy>> => {
  return api({
    method: 'PUT',
    endpoint: `/permissions/${policy.publicId}`,
    data: {
      role,
      context: { projectId }
    }
  })
}

api.deleteAccessPolicy = async (projectId: string, policy: IAccessPolicy) => {
  return api({
    method: 'DELETE',
    endpoint: `/permissions/${policy.publicId}`,
    data: {
      context: { projectId }
    }
  })
}

api.updateViewsProcessPermissions = async (
  resourceId: string,
  viewerProjectPermissionForSelects: boolean
): Promise<ResponsePayload<IAccessPolicy[]>> => {
  return api({
    method: 'PUT',
    endpoint: `/document/sync-views-permissions`,
    data: {
      resourceId,
      viewerProjectPermissionForSelects
    }
  })
}

api.getProjectMembers = async (projectId: string): Promise<ResponsePayload<IProjectMember[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/members`
  })
}

api.getInvitedMembers = async (projectId: string): Promise<ResponsePayload<IMemberInvite[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/invited-members`
  })
}

api.deleteProjectInvite = async (projectId: string, inviteId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/hub/${projectId}/invite/${inviteId}`
  })
}

api.resendProjectInvite = async (projectId: string, inviteId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/hub/${projectId}/invite/${inviteId}`
  })
}

api.addUserTag = async (user: ISummaryUser, tagReferenceId: string): Promise<ResponsePayload<ITag>> => {
  return api({
    method: 'POST',
    endpoint: `/user/${user.publicId}/tags`,
    data: {
      tagReferenceId
    }
  })
}

api.deleteUserTag = async (user: ISummaryUser, tag: ITag) => {
  return api({
    method: 'DELETE',
    endpoint: `/user/${user.publicId}/tags/${tag.publicId}`
  })
}

api.bulkSetTags = async (
  user: ISummaryUser | IUserObject,
  projectPublicId: string,
  tagReferenceIds: string[],
  tableId: string
): Promise<ResponsePayload<ITag[]>> => {
  return api({
    method: 'PUT',
    endpoint: `/user/${user.publicId}/tags`,
    data: {
      tagReferenceIds,
      projectId: projectPublicId,
      tableId: tableId
    }
  })
}

export interface NewPoliciesRequest extends IContext {
  role: UserRoles
  resourceKind: AccessResourceKinds
  resourceId: string
  attributeKind: AccessAttributeKinds
  attributeIds?: string[]
  tagReferenceIds?: string[]
}
api.bulkPermissionsResource = async (policy: NewPoliciesRequest): Promise<ResponsePayload<IAccessPolicy[]>> => {
  return api({
    method: 'PUT',
    endpoint: `/permissions/all`,
    data: policy
  })
}

api.updateProjectMemberRole = async (
  user: ISummaryUser,
  project: IProject,
  role: string
): Promise<ResponsePayload<IProjectMember>> => {
  return api({
    method: 'PUT',
    endpoint: `/hub/${project.publicId}/change-user-role/${user.firebaseUserId}`,
    data: {
      role
    }
  })
}

api.deleteProjectMember = async (user: ISummaryUser, project: IProject) => {
  return api({
    method: 'DELETE',
    endpoint: `/hub/${project.publicId}/remove-user/${user.firebaseUserId}`
  })
}

api.loginUser = async (email: string, password: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/user/authenticate`,
    data: {
      email,
      password
    }
  })
}

api.verifyAccount = async (token: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/user/verify-account`,
    data: {
      token
    }
  })
}

api.sendEmailVerification = async (email: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/user/send-verification-email`,
    data: {
      email
    }
  })
}

api.sendPasswordReset = async (email: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/user/send-password-reset-email`,
    data: {
      email
    }
  })
}

api.resetPassword = async (token: string, password: string): Promise<ResponsePayload<null>> => {
  return api({
    method: 'POST',
    endpoint: `/user/reset-password`,
    data: {
      token,
      password
    }
  })
}

api.completeGoogleOAuth = async (code: string, state?: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'GET',
    endpoint: `/identities/google/complete`,
    data: {
      code,
      state
    }
  })
}

api.getGoogleOauthConnectUrl = async (processTemplateId?: string): Promise<ResponsePayload<IOAuthConnectUrl>> => {
  const endpoint = processTemplateId
    ? `/identities/google/connect?template=${processTemplateId}`
    : `/identities/google/connect`

  return api({
    method: 'GET',
    endpoint
  })
}

api.completeMicrosoftOAuth = async (code: string, state?: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'GET',
    endpoint: `/identities/microsoft/complete`,
    data: {
      code,
      state
    }
  })
}

api.getMicrosoftOauthConnectUrl = async (processTemplateId?: string): Promise<ResponsePayload<IOAuthConnectUrl>> => {
  const endpoint = processTemplateId
    ? `/identities/microsoft/connect?template=${processTemplateId}`
    : `/identities/microsoft/connect`
  return api({
    method: 'GET',
    endpoint
  })
}

api.getSSOConnectUrl = async (email: string): Promise<ResponsePayload<{ ssoRedirectURL: string }>> => {
  return api({
    method: 'GET',
    endpoint: `/identities/sso/connect?email=${email}`
  })
}

api.getAllowedLoginMethods = async (email: string): Promise<ResponsePayload<{ allowedMethods: string[] }>> => {
  return api({
    method: 'GET',
    endpoint: `/identities/allowed-methods?email=${email}`
  })
}

api.getAllUsers = async (
  email: string,
  subscriptionLevel: number | undefined,
  page: number,
  perPage: number
): Promise<ResponsePayload<IUserObject[]>> => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage)
  })
  if (email) params.set('email', email)
  if (subscriptionLevel !== undefined) params.set('subscriptionLevel', String(subscriptionLevel))
  return api({
    method: 'GET',
    endpoint: `/admin/users?${params.toString()}`
  })
}

api.getAllProjects = async (simple = false): Promise<ResponsePayload<IProject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/admin/hubs?simple=${simple}`
  })
}

api.getMasqueradeCredentials = async (
  user: IUserObject
): Promise<ResponsePayload<{ token: string; expires: number }>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${user.publicId}/masquerade`
  })
}

api.forceSupportAccess = async (
  userPublicId: string,
  reason: string
): Promise<ResponsePayload<{ allowSupportAccess: boolean; allowSupportAccessExpiresAt: string }>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${userPublicId}/force-support-access`,
    data: { reason }
  })
}

api.logoutUser = async () => {
  return api({
    method: 'POST',
    endpoint: `/user/logout`
  })
}

api.refreshSession = async (): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/user/refresh-token`
  })
}

api.createProjectSecret = async (
  project: IProject,
  name: string,
  value: string
): Promise<ResponsePayload<IProjectSecret>> => {
  return api({
    method: 'POST',
    endpoint: `/hub/${project.publicId}/secrets`,
    data: {
      name,
      value
    }
  })
}

api.deleteProjectSecret = async (project: IProject, secret: IProjectSecret): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/hub/${project.publicId}/secrets/${secret.publicId}`
  })
}

api.updateProjectSecret = async (
  project: IProject,
  secret: IProjectSecret,
  name: string,
  value: string
): Promise<ResponsePayload<IProjectSecret>> => {
  return api({
    method: 'PUT',
    endpoint: `/hub/${project.publicId}/secrets/${secret.publicId}`,
    data: {
      name,
      value
    }
  })
}

api.setup2FA = async (): Promise<ResponsePayload<I2FaSetup>> => {
  return api({ method: 'POST', endpoint: `/user/2fa/setup` })
}

api.complete2FaSetup = async (code: string): Promise<ResponsePayload<string[]>> => {
  return api({ method: 'POST', endpoint: `/user/2fa/setup/complete`, data: { code } })
}

api.disable2FaSetup = async (code: string): Promise<ResponsePayload<void>> => {
  return api({ method: 'POST', endpoint: `/user/2fa/remove`, data: { code } })
}

api.getTempUserToken = async (email: string, password: string): Promise<ResponsePayload<{ code: string }>> => {
  return api({ method: 'POST', endpoint: `/user/token`, data: { email, password } })
}

api.loginWith2Fa = async (twoFactorCode: string, token: string): Promise<ResponsePayload<IUserObject>> => {
  return api({ method: 'POST', endpoint: `/user/2fa/authenticate`, data: { twoFactorCode, token } })
}

api.getUserSessions = async (): Promise<ResponsePayload<IUserSession[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/sessions`
  })
}

api.deleteUserSession = async (publicId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/user/sessions/${publicId}`
  })
}

api.updateUserTheme = async (theme: ThemePreference): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'PUT',
    endpoint: `/user/account`,
    data: { theme }
  })
}

api.updateUserAccount = async (
  oldPassword?: string,
  password?: string,
  passwordConfirm?: string,
  twoFactorCode?: string,
  allowSupportAccessExpiresAt?: string | null,
  optOutWelcomeEmail?: boolean,
  optOutSyncEmail?: boolean,
  optOutAiEmail?: boolean,
  optOutHubEmail?: boolean,
  optOutDuplicationEmail?: boolean,
  optOutDeprecationEmail?: boolean,
  optOutMentionEmail?: boolean
): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'PUT',
    endpoint: `/user/account`,
    data: {
      oldPassword,
      password,
      passwordConfirm,
      twoFactorCode,
      allowSupportAccessExpiresAt,
      optOutWelcomeEmail,
      optOutSyncEmail,
      optOutAiEmail,
      optOutHubEmail,
      optOutDuplicationEmail,
      optOutDeprecationEmail,
      optOutMentionEmail
    }
  })
}
interface INewApiKey {
  tempKey: string
  hash: string
  name: string
  prefix: string
  publicId: string
  accessLevel: number
  siteRestrictions: string[]
  projectRestrictions: string[]
  tableRestrictions: string[]
  documentRestrictions: string[]
}
api.updateApiKey = async (apiKey: IApiKey): Promise<ResponsePayload<IApiKey>> => {
  return api({
    method: 'PUT',
    endpoint: '/user/apikey/' + apiKey.publicId,
    data: {
      name: apiKey.name,
      accessLevel: apiKey.accessLevel,
      siteRestrictions: apiKey.siteRestrictions,
      projectRestrictions: apiKey.projectRestrictions,
      tableRestrictions: apiKey.tableRestrictions,
      documentRestrictions: apiKey.documentRestrictions
    }
  })
}

api.getIntegrationRedirects = async (): Promise<ResponsePayload<IIntegrationRedirects>> => {
  return api({
    method: 'GET',
    endpoint: '/integrations/redirects'
  })
}

api.getIntegrationCatalogue = async (): Promise<ResponsePayload<IIntegrationCatalogue>> => {
  return api({
    method: 'GET',
    endpoint: '/integrations/catalogue'
  })
}

api.getProviderOptions = async (
  provider: string,
  fieldKey: string,
  params: Record<string, any>
): Promise<ResponsePayload<FieldOption[]>> => {
  return api({
    method: 'GET',
    endpoint: `/integrations/${provider}/fields/${fieldKey}/options`,
    data: Object.keys(params).length > 0 ? params : undefined
  })
}

api.getAccessToken = async (
  provider: string,
  authorizationCode: string,
  region?: string
): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/integrations/${provider}/get-access-token`,
    data: {
      authorizationCode,
      region
    }
  })
}

api.saveApiKey = async (
  provider: string,
  credentials: Record<string, string>,
  region?: string
): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/integrations/${provider}/save-api-key`,
    data: { credentials, region }
  })
}

api.disconnectAccount = async (provider: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'DELETE',
    endpoint: `/integrations/${provider}/disconnect`
  })
}

api.syncIntegration = async (
  tableId: string,
  providerName: string,
  data: IIntegrationData,
  updatingIntegration: boolean
): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: updatingIntegration
      ? `/table/${tableId}/sync/${providerName}/update`
      : `/table/${tableId}/sync/${providerName}`,
    data
  })
}

api.manualSyncIntegration = async (tableId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'GET',
    endpoint: `/table/${tableId}/sync/manual`
  })
}

api.getSyncIntegrationInfo = async (
  tableId: string
): Promise<ResponsePayload<{ isSyncing: boolean; lastSync: string | null; failedSyncAttempts: number }>> => {
  return api({ method: 'GET', endpoint: `/table/${tableId}/sync/info` })
}
api.deleteIntegration = async (tableId: string, provider: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/table/${tableId}/sync/${provider}`
  })
}

api.createApiKey = async (apiKey: Partial<IApiKey>): Promise<ResponsePayload<INewApiKey>> => {
  return api({
    method: 'POST',
    endpoint: '/user/apikey',
    data: {
      name: apiKey.name,
      accessLevel: apiKey.accessLevel,
      siteRestrictions: apiKey.siteRestrictions,
      projectRestrictions: apiKey.projectRestrictions,
      tableRestrictions: apiKey.tableRestrictions,
      documentRestrictions: apiKey.documentRestrictions
    }
  })
}

api.deleteApiKey = async (apiKey: Partial<IApiKey>): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: '/user/apikey/' + apiKey.publicId
  })
}

interface UserSearchParameters {
  query?: string
  processId?: string
  projectId?: string
  tableViewId?: string
}
api.searchUsers = async (params: UserSearchParameters): Promise<ResponsePayload<ISummaryUser[]>> => {
  return api({ method: 'GET', endpoint: `/user/search`, data: params })
}

api.getProjectProcesses = async (projectId: string): Promise<ResponsePayload<IProcess[]>> => {
  return api({
    method: 'GET',
    endpoint: `/hub/${projectId}/documents`
  })
}

api.getCeleryStats = async (): Promise<ResponsePayload<ICeleryStats>> => {
  return api({
    method: 'GET',
    endpoint: `/admin/celery/stats`
  })
}

api.redisStats = async () => {
  return api({
    method: 'GET',
    endpoint: `/admin/redis/stats`
  })
}

api.flushRedis = async (): Promise<ResponsePayload<string>> => {
  return api({
    method: 'GET',
    endpoint: `/admin/redis/flush`
  })
}

api.executeCeleryQueueActions = async (action: string, queue?: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'GET',
    endpoint: `/admin/celery/${action}${queue ? `/${queue}` : ''}`
  })
}

api.getUserStats = async (): Promise<ResponsePayload<Record<string, number>>> => {
  return api({
    method: 'GET',
    endpoint: '/admin/user/stats'
  })
}

api.getTotalUsers = async (): Promise<ResponsePayload<{ totalUsers: number; paidUsers: number }>> => {
  return api({
    method: 'GET',
    endpoint: '/admin/total-users'
  })
}

api.verifyUserEmail = async (userId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${userId}/verify`
  })
}

api.deleteUser = async (userId: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'DELETE',
    endpoint: `/admin/users/${userId}`
  })
}

api.restoreUser = async (userId: string): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${userId}/restore`
  })
}

api.adminRemove2fa = async (userId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${userId}/2fa/remove`
  })
}

api.adminClearRateLimit = async (userId: string): Promise<ResponsePayload<{ deletedKeys: number }>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${userId}/rate-limit/clear`
  })
}

api.adminGetUserTeams = async (
  userId: string
): Promise<
  ResponsePayload<{ publicId: string; name: string; seatLimit: number; seatsUsed: number; isDefault: boolean }[]>
> => {
  return api({
    method: 'GET',
    endpoint: `/admin/users/${userId}/teams`
  })
}

api.adminUpdateUser = async (
  userId: string,
  name: string,
  email: string,
  password: string | null,
  subscriptionLevel: number,
  eligibleForFreeTrial: boolean,
  isSuperAdmin: boolean,
  teamId: string | null = null
): Promise<ResponsePayload<IUserObject>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/users/${userId}/update`,
    data: {
      name,
      email,
      password: password === undefined || password === null || password === '' ? null : password,
      subscriptionLevel,
      eligibleForFreeTrial,
      isSuperAdmin,
      teamId
    }
  })
}

api.getProjectStats = async (): Promise<ResponsePayload<Record<string, number>>> => {
  return api({
    method: 'GET',
    endpoint: '/admin/hub/stats'
  })
}

api.requestPermission = async (project: string, type: string, id: string): Promise<ResponsePayload<boolean>> => {
  return api({
    method: 'GET',
    endpoint: `/permissions/request/${project}/${type}/${id}`
  })
}

api.getWhereTableUsed = async (publicId: string): Promise<ResponsePayload<IUsed[]>> => {
  return api({
    method: 'GET',
    endpoint: `/table/${publicId}/used`
  })
}

api.getColumnUsedViews = async (
  tableId: string,
  columnId: string
): Promise<ResponsePayload<ITableViewWithColumns[]>> => {
  return api({
    method: 'GET',
    endpoint: `/table/${tableId}/column/${columnId}/views`
  })
}

api.upsertRows = async (
  tableViewId: string,
  rows: Array<{ rowData: Record<string, unknown>; sortOrder?: number }>,
  upsertColumnName: string
): Promise<ResponsePayload<ITableRow[]>> => {
  return api({
    method: 'POST',
    endpoint: `/table/views/${tableViewId}/rows/upsert`,
    data: {
      rows,
      upsertColumnName
    }
  })
}

api.getTemplates = async (): Promise<ResponsePayload<Partial<IProcessObject>[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/templates`
  })
}

interface IIntegrationPassthrough {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  path: string
  sourceSystem: string
  headers?: Record<string, string>
  data?: Record<string, any>
}

api.integrationPassthrough = async (
  args: IIntegrationPassthrough
): Promise<ResponsePayload<IIntegrationPassthroughResponse>> => {
  return api({
    method: 'POST',
    endpoint: `/integrations/passthrough`,
    data: args
  })
}

api.getSubscriptionComparison = async (): Promise<ResponsePayload<ISubscriptionComparison[]>> => {
  return api({
    method: 'GET',
    endpoint: `/subscription/comparison`
  })
}

api.allocateLicense = async (email: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/subscription/allocate`,
    data: {
      email
    }
  })
}

api.removeLicense = async (email: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/subscription/remove`,
    data: {
      email
    }
  })
}

api.validateFormula = async (
  viewId: string,
  columnId: string,
  formula: string
): Promise<ResponsePayload<IFormulaValidation>> => {
  return api({
    method: 'POST',
    endpoint: `/table/views/${viewId}/column/${columnId}/validate-formula`,
    data: { formula }
  })
}

api.getFormulaHelper = async (viewId: string, columnId: string): Promise<ResponsePayload<IFormulaHelper[]>> => {
  return api({
    method: 'GET',
    endpoint: `/table/views/${viewId}/column/${columnId}/formula-info`
  })
}

// A hub's section heading styles, one per level, in order. Each call returns the hub's styles as they
// now are.
api.addHeadingStyle = async (projectId: string): Promise<ResponsePayload<IProject>> =>
  api({ method: 'POST', endpoint: `/hub/${projectId}/add_heading_styling` })

api.updateHeadingStyle = async (projectId: string, style: IHeadingStyle): Promise<ResponsePayload<IProject>> =>
  api({
    method: 'POST',
    endpoint: `/hub/${projectId}/style/${style.publicId}`,
    data: {
      fontSize: style.fontSize,
      bold: style.bold,
      italic: style.italic,
      underline: style.underline,
      colour: style.colour,
      numberingStyle: style.numberingStyle,
      startAt0: style.startAt0
    }
  })

/** Removes the deepest heading level's style. */
api.removeLastHeadingStyle = async (projectId: string): Promise<ResponsePayload<IProject>> =>
  api({ method: 'DELETE', endpoint: `/hub/${projectId}/delete_top_style` })

// Give every column in the hub the hub's default header colours, or its default format for one kind
// of date column.
api.applyDefaultColumnColours = async (projectId: string): Promise<ResponsePayload<null>> =>
  api({ method: 'POST', endpoint: `/hub/${projectId}/set-column-coloring` })

api.applyDefaultDateFormat = async (projectId: string, kind: 'date' | 'datetime'): Promise<ResponsePayload<null>> =>
  api({ method: 'POST', endpoint: `/hub/${projectId}/set-column-format/${kind}` })

api.uploadTemplateFile = async (projectId: string, file: File): Promise<ResponsePayload<IProject>> => {
  const body = new FormData()
  body.set('file', file)

  return api({
    method: 'POST',
    endpoint: `/hub/${projectId}/upload-template`,
    rawBody: true,
    data: body
  })
}

api.getDuplicatedChildren = async (
  publicId: string,
  type: 'process' | 'project' | 'table'
): Promise<ResponsePayload<IDuplicatedChild[]>> => {
  let endpoint = ''
  switch (type) {
    case 'process':
      endpoint = `/document/${publicId}/duplicated-children`
      break
    case 'project':
      endpoint = `/hub/${publicId}/duplicated-children`
      break
    case 'table':
      endpoint = `/table/${publicId}/duplicated-children`
      break
  }

  return api({
    method: 'GET',
    endpoint
  })
}

api.getAdminOrganisations = async (
  page: number,
  perPage: number,
  search: string,
  planTiers: string[],
  showAll = false
): Promise<ResponsePayload<IOrganisation[]>> => {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage)
  })
  if (search) params.set('search', search)
  planTiers.forEach((tier) => params.append('planTier', tier))
  if (showAll) params.set('all', 'true')
  return api({
    method: 'GET',
    endpoint: `/admin/organisations?${params.toString()}`
  })
}

api.createOrganisation = async (organisation: IOrganisation): Promise<ResponsePayload<IOrganisation>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/organisations`,
    data: {
      name: organisation.name,
      enforceLogin: organisation.enforceLogin,
      domains: organisation.domains,
      users: organisation.users.map((user) => {
        return {
          email: user.user.email,
          role: user.role
        }
      }),
      idpSsoUrl: organisation.idpSsoUrl,
      idpIssuer: organisation.idpIssuer,
      idpCertificate: organisation.idpCertificate,
      enableUserApiKeys: organisation.enableUserApiKeys,
      disablePersonalViewSaving: organisation.disablePersonalViewSaving,
      restrictHubCreation: organisation.restrictHubCreation,
      restrictSyncConfiguration: organisation.restrictSyncConfiguration,
      planTier: organisation.planTier,
      multiYearContract: organisation.multiYearContract
    }
  })
}

api.adminUpdateOrganisation = async (
  organisationId: string,
  organisation: IOrganisation
): Promise<ResponsePayload<IOrganisation>> => {
  return api({
    method: 'PUT',
    endpoint: `/admin/organisations/${organisationId}`,
    data: {
      name: organisation.name,
      enforceLogin: organisation.enforceLogin,
      domains: organisation.domains,
      users: organisation.users,
      idpSsoUrl: organisation.idpSsoUrl,
      idpIssuer: organisation.idpIssuer,
      idpCertificate: organisation.idpCertificate,
      enableUserApiKeys: organisation.enableUserApiKeys,
      disablePersonalViewSaving: organisation.disablePersonalViewSaving,
      restrictHubCreation: organisation.restrictHubCreation,
      restrictSyncConfiguration: organisation.restrictSyncConfiguration,
      planTier: organisation.planTier,
      multiYearContract: organisation.multiYearContract
    }
  })
}

export const isUserToSiteArray = (users: ISiteUserRow[] | IUserToSite[]): users is IUserToSite[] => {
  return users.length > 0 && 'user' in users[0]
}

api.updateOrganisation = async (
  organisationId: string,
  organisation: IOrganisation
): Promise<ResponsePayload<IOrganisation>> => {
  return api({
    method: 'PUT',
    endpoint: `/organisation/${organisationId}`,
    data: {
      name: organisation.name,
      enforceLogin: organisation.enforceLogin,
      idpSsoUrl: organisation.idpSsoUrl,
      idpIssuer: organisation.idpIssuer,
      idpCertificate: organisation.idpCertificate,
      users: organisation.users.map((user) => {
        return {
          email: user.user.email,
          role: user.role
        }
      }),
      enableUserApiKeys: organisation.enableUserApiKeys,
      disablePersonalViewSaving: organisation.disablePersonalViewSaving,
      restrictHubCreation: organisation.restrictHubCreation,
      restrictSyncConfiguration: organisation.restrictSyncConfiguration
    }
  })
}

api.deleteOrganisation = async (organisationId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/admin/organisations/${organisationId}`
  })
}

api.getOrganisationHubs = async (organisationId: string): Promise<ResponsePayload<IProject[]>> => {
  return api({
    method: 'GET',
    endpoint: `/organisation/${organisationId}/hubs`
  })
}

api.getOrganisationApiKeys = async (organisationId: string): Promise<ResponsePayload<IApiKey[]>> => {
  return api({
    method: 'GET',
    endpoint: `/organisation/${organisationId}/api-key`
  })
}

api.deleteOrganisationApiKey = async (organisationId: string, apiKeyId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/organisation/${organisationId}/api-key/${apiKeyId}`
  })
}

api.createOrganisationApiKey = async (
  organisationId: string,
  apiKey: Partial<IApiKey>
): Promise<ResponsePayload<INewApiKey>> => {
  return api({
    method: 'POST',
    endpoint: `/organisation/${organisationId}/api-key`,
    data: {
      name: apiKey.name,
      accessLevel: apiKey.accessLevel,
      siteRestrictions: apiKey.siteRestrictions,
      projectRestrictions: apiKey.projectRestrictions,
      tableRestrictions: apiKey.tableRestrictions,
      documentRestrictions: apiKey.documentRestrictions
    }
  })
}

api.updateOrganisationApiKey = async (organisationId: string, apiKey: IApiKey): Promise<ResponsePayload<IApiKey>> => {
  return api({
    method: 'PUT',
    endpoint: `/organisation/${organisationId}/api-key/${apiKey.publicId}`,
    data: {
      name: apiKey.name,
      accessLevel: apiKey.accessLevel,
      siteRestrictions: apiKey.siteRestrictions,
      projectRestrictions: apiKey.projectRestrictions,
      tableRestrictions: apiKey.tableRestrictions,
      documentRestrictions: apiKey.documentRestrictions
    }
  })
}

api.getUserSites = async (): Promise<ResponsePayload<ISite[]>> => {
  return api({
    method: 'GET',
    endpoint: `/user/sites`
  })
}

api.getOrganisationSites = async (organisationId: string): Promise<ResponsePayload<ISite[]>> => {
  return api({
    method: 'GET',
    endpoint: `/organisation/${organisationId}/sites`
  })
}

api.createSite = async (
  organisationId: string,
  name: string,
  users: ISiteUserRow[] | undefined,
  teamId: string
): Promise<ResponsePayload<ISite>> => {
  return api({
    method: 'POST',
    endpoint: `/organisation/${organisationId}/site`,
    data: {
      name,
      ...(users !== undefined && { users }),
      teamId
    }
  })
}

api.updateSite = async (
  organisationId: string,
  siteId: string,
  name: string,
  users?: ISiteUserRow[],
  teamId?: string
): Promise<ResponsePayload<ISite>> => {
  return api({
    method: 'PUT',
    endpoint: `/organisation/${organisationId}/site/${siteId}`,
    data: {
      name,
      ...(users !== undefined && { users }),
      ...(teamId !== undefined && { teamId })
    }
  })
}

api.deleteSite = async (organisationId: string, siteId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/organisation/${organisationId}/site/${siteId}`
  })
}

api.allocateOrganisationLicense = async (organisationId: string, userId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/organisation/${organisationId}/user/${userId}/allocate-license`
  })
}

api.removeOrganisationLicense = async (organisationId: string, userId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/organisation/${organisationId}/user/${userId}/remove-license`
  })
}

api.deleteOrganisationUser = async (
  organisationId: string,
  userId: string
): Promise<ResponsePayload<{ deleted: boolean }>> => {
  return api({
    method: 'DELETE',
    endpoint: `/organisation/${organisationId}/user/${userId}`
  })
}

api.getStuckLocks = async (): Promise<ResponsePayload<IStuckLock[]>> => {
  return api({
    method: 'GET',
    endpoint: `/admin/db/locks`
  })
}

api.cancelDbTransaction = async (transactionId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'POST',
    endpoint: `/admin/db/cancel-transaction/${transactionId}`
  })
}

api.getOrganisation = async (organisationId: string): Promise<ResponsePayload<IOrganisation>> => {
  return api({
    method: 'GET',
    endpoint: `/organisation/${organisationId}`
  })
}

// Organisation Teams
api.getOrganisationTeams = async (organisationId: string): Promise<ResponsePayload<ITeamsListResponse>> => {
  return api({
    method: 'GET',
    endpoint: `/organisation/${organisationId}/teams`
  })
}

api.createOrganisationTeam = async (
  organisationId: string,
  data: { name: string; seatLimit: number; managerEmails: string[]; memberEmails?: string[] }
): Promise<ResponsePayload<IOrganisationTeam>> => {
  return api({
    method: 'POST',
    endpoint: `/organisation/${organisationId}/teams`,
    data
  })
}

api.getOrganisationTeamDetail = async (
  organisationId: string,
  teamId: string
): Promise<ResponsePayload<IOrganisationTeamDetail>> => {
  return api({
    method: 'GET',
    endpoint: `/organisation/${organisationId}/teams/${teamId}`
  })
}

api.updateOrganisationTeam = async (
  organisationId: string,
  teamId: string,
  data: { name?: string; seatLimit?: number; managerEmails?: string[]; memberEmails?: string[] }
): Promise<ResponsePayload<IOrganisationTeam>> => {
  return api({
    method: 'PUT',
    endpoint: `/organisation/${organisationId}/teams/${teamId}`,
    data
  })
}

api.deleteOrganisationTeam = async (organisationId: string, teamId: string): Promise<ResponsePayload<string>> => {
  return api({
    method: 'DELETE',
    endpoint: `/organisation/${organisationId}/teams/${teamId}`
  })
}

// Team Seat Management
api.getTeamSeats = async (teamId: string): Promise<ResponsePayload<IOrganisationTeamDetail>> => {
  return api({
    method: 'GET',
    endpoint: `/teams/${teamId}/seats`
  })
}

api.assignTeamSeat = async (teamId: string, email: string): Promise<ResponsePayload<IOrganisationTeamDetail>> => {
  return api({
    method: 'POST',
    endpoint: `/teams/${teamId}/seats/assign`,
    data: { email }
  })
}

api.revokeTeamSeat = async (teamId: string, email: string): Promise<ResponsePayload<IOrganisationTeamDetail>> => {
  return api({
    method: 'POST',
    endpoint: `/teams/${teamId}/seats/revoke`,
    data: { email }
  })
}

api.agentChatStream = (
  message: string,
  onEvent: (event: IAgentStreamEvent) => void,
  conversationId?: string,
  tableViewContextId?: string,
  documentContextId?: string,
  hubContextId?: string,
  webSearch?: boolean,
  codeExecution?: boolean,
  files?: File[],
  resume?: boolean
): AbortController => {
  const controller = new AbortController()
  const token = getAuthToken()

  const jsonPayload = {
    message,
    conversationId,
    tableViewContextId,
    documentContextId,
    hubContextId,
    webSearch: webSearch || false,
    codeExecution: codeExecution || false,
    resume: resume || false
  }

  const headers: Record<string, string> = token ? { Authorization: token } : {}
  let body: BodyInit

  if (files && files.length > 0) {
    const formData = new FormData()
    formData.append('data', JSON.stringify(jsonPayload))
    for (const file of files) {
      formData.append('files', file)
    }
    body = formData
  } else {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(jsonPayload)
  }

  fetch(`${apiPrefix}/ai/chat`, {
    method: 'POST',
    headers,
    body,
    signal: controller.signal
  })
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          markLoggedOut()
          safeLocalStorage.removeItem(AUTH_KEY)
          safeLocalStorage.removeItem(FREE_TRIAL_BANNER_DISMISSED_KEY)
          clearMasqueradeToken()
          Sentry.setUser(null)
          saveCurrentUrlForRedirect()
          window.location.href = `${SIGN_IN}?reason=sessionExpired`
          return
        }
        let errorMessage = "Morta couldn't reach the AI assistant. Try again in a moment."
        try {
          const errorJson = (await response.json()) as ResponseError
          if (errorJson?.detail?.message) {
            errorMessage = errorJson.detail.message
          }
        } catch {
          // Could not parse error response, use default message
        }
        onEvent({ type: 'error', content: errorMessage })
        return
      }

      const reader = response.body?.getReader()
      if (!reader) return

      const decoder = new TextDecoder()
      let buffer = ''
      let done = false

      while (!done) {
        const result = await reader.read()
        done = result.done
        if (done) break

        buffer += decoder.decode(result.value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed.startsWith('data: ')) continue
          try {
            const event: IAgentStreamEvent = JSON.parse(trimmed.slice(6))
            onEvent(event)
          } catch {
            // skip malformed events
          }
        }
      }
    })
    .catch((err) => {
      if (err.name !== 'AbortError') {
        onEvent({ type: 'error', content: 'Connection lost. Please try again.' })
      }
    })

  return controller
}

api.agentChatDelete = async (conversationId: string): Promise<void> => {
  await api({
    method: 'DELETE',
    endpoint: `/ai/chat/${conversationId}`
  })
}

api.agentChatVote = async (messageId: string, vote: number, feedback?: string): Promise<void> => {
  await api({
    method: 'PATCH',
    endpoint: `/ai/chat/messages/${messageId}/vote`,
    data: { vote, feedback }
  })
}

api.agentChatToolOutcome = async (
  messageId: string,
  toolUseId: string,
  implemented: boolean,
  error?: Record<string, any>
): Promise<void> => {
  await api({
    method: 'PATCH',
    endpoint: `/ai/chat/messages/${messageId}/tools/${toolUseId}`,
    data: { implemented, ...(error ? { error } : {}) }
  })
}

export interface IConversationListItem {
  publicId: string
  preview: string
  messageCount: number
  hubContextId: string | null
  viewContextId: string | null
  documentContextId: string | null
  createdAt: string
  updatedAt: string
}

export interface IConversationMessageTool {
  publicId: string
  skillId: string
  arguments: Record<string, any> | null
  output: Record<string, any> | null
  implemented: boolean | null
  error: Record<string, any> | null
}

export interface IConversationMessage {
  publicId: string
  role: 'user' | 'agent'
  content: string
  thinking: string | null
  tools: IConversationMessageTool[]
  createdAt: string
}

export interface IConversationDetail extends IConversationListItem {
  messages: IConversationMessage[]
}

api.getConversations = async (
  page: number = 1,
  perPage: number = 20
): Promise<ResponsePayload<IConversationListItem[]>> => {
  return api({
    method: 'GET',
    endpoint: '/ai/chat/conversations',
    data: { page, perPage }
  })
}

api.getConversation = async (conversationId: string): Promise<ResponsePayload<IConversationDetail>> => {
  return api({
    method: 'GET',
    endpoint: `/ai/chat/conversations/${conversationId}`
  })
}

api.enableSupportAccess = async (): Promise<ResponsePayload<{ enabled: boolean; expires_at: string }>> => {
  return api({
    method: 'POST',
    endpoint: '/user/enable-support-access'
  })
}

api.getAdminConversations = async (): Promise<ResponsePayload<any[]>> => {
  return api({ method: 'GET', endpoint: '/admin/conversations' })
}

api.getAdminConversation = async (conversationId: string): Promise<ResponsePayload<any>> => {
  return api({ method: 'GET', endpoint: `/admin/conversations/${conversationId}` })
}

api.getAdminConversationsExport = async (params?: {
  limit?: number
  since?: string
}): Promise<ResponsePayload<any[]>> => {
  const query = new URLSearchParams()
  if (params?.limit) query.set('limit', String(params.limit))
  if (params?.since) query.set('since', params.since)
  const qs = query.toString()
  return api({ method: 'GET', endpoint: `/admin/conversations/export${qs ? `?${qs}` : ''}` })
}
